import React, { useEffect, Suspense } from "react";
import { BrowserRouter, Routes, Route, Navigate, Link, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { pageView } from "@/lib/analytics";
import "./App.css";
import "./index.css";

import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import LandingPage from "@/pages/LandingPage";
import LoginPage from "@/pages/LoginPage";
import AuthCallback from "@/pages/AuthCallback";
import DefinirSenhaPage from "@/pages/DefinirSenhaPage";
import Dashboard from "@/pages/Dashboard";
import ProcessosPanel from "@/pages/ProcessosPanel";
import ProcessoDetails from "@/pages/ProcessoDetails";
import IntimacoesPanel from "@/pages/IntimacoesPanel";
import AgendaPage from "@/pages/AgendaPage";
import KanbanPage from "@/pages/KanbanPage";
import ConfiguracoesPage from "@/pages/ConfiguracoesPage";
import PlanoPage from "@/pages/PlanoPage";
import TermosDeUso from "@/pages/TermosDeUso";
import PoliticaDePrivacidade from "@/pages/PoliticaDePrivacidade";
import JurisprudenciasPage from "@/pages/JurisprudenciasPage";
import ClientesPage from "@/pages/ClientesPage";
import ClienteDetails from "@/pages/ClienteDetails";
import FinanceiroPage from "@/pages/FinanceiroPage";
import DocumentosPage from "@/pages/DocumentosPage";
import ChatPage from "@/pages/ChatPage";
import UsoIAPage from "@/pages/UsoIAPage";
import ComecarPage from "@/pages/ComecarPage";
import PreCadastroPage from "@/pages/PreCadastroPage";
import ConfirmandoCadastroPage from "@/pages/ConfirmandoCadastroPage";
// Lazy: admin (+recharts) só carrega quando acessado, fora do bundle principal
const AdminPage = React.lazy(() => import("@/pages/AdminPage"));

export function ProtectedRoute({ children, permiteSuspenso = false }) {
  const { user, loading } = useAuth();

  // While initial auth check is running, show spinner
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F4F7FB]">
        <div className="flex flex-col items-center gap-4">
          <div className="w-12 h-12 border-4 border-[#003882] border-t-transparent rounded-full animate-spin" />
          <p className="text-[#335480] text-lg font-medium">Carregando...</p>
        </div>
      </div>
    );
  }

  // Auth check done and no user → send to login
  if (!user) {
    return <Navigate to="/login" replace />;
  }

  // Escritório suspenso → bloqueia o app e mostra a tela de regularização.
  //
  // Menos a própria tela de planos: ela é a saída. Sem a exceção, o botão
  // "Escolher plano" levava a /plano, o guard interceptava e devolvia a mesma
  // tela — o usuário ficava preso num laço, sem como assinar nem sair.
  if (user.workspace_suspended && !permiteSuspenso) {
    return <SuspendedScreen />;
  }

  return children;
}

function SuspendedScreen() {
  const { logout, user } = useAuth();
  const [loading, setLoading] = React.useState(false);
  const openPortal = async () => {
    setLoading(true);
    try {
      const axios = (await import("axios")).default;
      const { API } = await import("@/contexts/AuthContext");
      const res = await axios.post(`${API}/payments/portal`, {}, { withCredentials: true });
      window.location.href = res.data.url;
    } catch {
      setLoading(false);
    }
  };
  // Quem nunca assinou não tem o que "regularizar": mandá-lo ao portal do
  // Stripe seria um beco sem saída, porque não existe assinatura lá. Esse é o
  // caso de quem criou a conta pelo Google depois que o FREE saiu de venda.
  const nuncaAssinou = user?.suspended_reason === "plano_indisponivel";

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#F4F7FB] px-4">
      <div className="max-w-md w-full bg-white border border-[#D1DEEE] rounded-2xl p-8 text-center">
        <div className="w-14 h-14 rounded-full bg-amber-100 flex items-center justify-center mx-auto mb-5">
          <span className="text-2xl">{nuncaAssinou ? "👋" : "⚠️"}</span>
        </div>
        <h1 className="text-2xl font-bold text-[#001533] mb-2" style={{ fontFamily: "Cabinet Grotesk" }}>
          {nuncaAssinou ? "Falta escolher seu plano" : "Acesso suspenso"}
        </h1>
        <p className="text-[#335480] mb-6">
          {nuncaAssinou
            ? "Sua conta foi criada. Escolha um plano para liberar o acesso ao escritório."
            : "Identificamos uma pendência no pagamento da sua assinatura. Regularize para reativar o acesso ao escritório."}
        </p>
        {user?.role !== "owner" ? (
          <p className="text-[#335480] text-sm mb-6">
            {nuncaAssinou
              ? "Peça ao proprietário do escritório para escolher um plano."
              : "Peça ao proprietário do escritório para regularizar o pagamento."}
          </p>
        ) : nuncaAssinou ? (
          <Link
            to="/plano"
            data-testid="escolher-plano-btn"
            className="block w-full h-12 leading-[48px] rounded-lg bg-[#003882] hover:bg-[#00285e] text-white font-semibold mb-3"
          >
            Escolher plano
          </Link>
        ) : (
          <button
            onClick={openPortal}
            disabled={loading}
            className="w-full h-12 rounded-lg bg-[#003882] hover:bg-[#00285e] text-white font-semibold mb-3"
          >
            {loading ? "Abrindo..." : "Regularizar pagamento"}
          </button>
        )}
        <button onClick={() => logout()} className="text-[#335480] hover:text-[#003882] text-sm">
          Sair
        </button>
      </div>
    </div>
  );
}

function RouteTracker() {
  const location = useLocation();
  useEffect(() => {
    pageView(location.pathname + location.search);
  }, [location.pathname, location.search]);
  return null;
}

function AppRouter() {
  return (
    <Routes>
      <Route path="/" element={<LandingPage />} />
      <Route path="/login" element={<LoginPage />} />
      {/* Destino do tráfego pago: página própria, sem menu e com cadastro na tela. */}
      <Route path="/comecar" element={<ComecarPage />} />
      {/* Volta do pagamento: a conta nasce aqui. */}
      <Route path="/cadastro/confirmando" element={<ConfirmandoCadastroPage />} />
      <Route path="/registro" element={<LoginPage mode="register" />} />
      <Route path="/convite/:invite_code" element={<LoginPage mode="register" />} />
      <Route path="/auth/callback" element={<AuthCallback />} />
      <Route path="/definir-senha" element={<DefinirSenhaPage />} />
      <Route
        path="/dashboard"
        element={<ProtectedRoute><Dashboard /></ProtectedRoute>}
      />
      <Route
        path="/processos"
        element={<ProtectedRoute><ProcessosPanel /></ProtectedRoute>}
      />
      <Route
        path="/processos/:id"
        element={<ProtectedRoute><ProcessoDetails /></ProtectedRoute>}
      />
      <Route path="/clientes" element={<ProtectedRoute><ClientesPage /></ProtectedRoute>} />
      <Route path="/clientes/:id" element={<ProtectedRoute><ClienteDetails /></ProtectedRoute>} />
      <Route path="/financeiro" element={<ProtectedRoute><FinanceiroPage /></ProtectedRoute>} />
      <Route path="/documentos" element={<ProtectedRoute><DocumentosPage /></ProtectedRoute>} />
      <Route path="/assistente" element={<ProtectedRoute><ChatPage /></ProtectedRoute>} />
      <Route path="/uso-ia" element={<ProtectedRoute><UsoIAPage /></ProtectedRoute>} />
      <Route
        path="/intimacoes"
        element={<ProtectedRoute><IntimacoesPanel /></ProtectedRoute>}
      />
      <Route
        path="/jurisprudencias"
        element={<ProtectedRoute><JurisprudenciasPage /></ProtectedRoute>}
      />
      <Route
        path="/agenda"
        element={<ProtectedRoute><AgendaPage /></ProtectedRoute>}
      />
      <Route
        path="/quadros"
        element={<ProtectedRoute><KanbanPage /></ProtectedRoute>}
      />
      <Route
        path="/configuracoes"
        element={<ProtectedRoute><ConfiguracoesPage /></ProtectedRoute>}
      />
      <Route
        path="/pre-cadastro"
        element={<ProtectedRoute><PreCadastroPage /></ProtectedRoute>}
      />
      <Route
        path="/plano"
        element={<ProtectedRoute permiteSuspenso><PlanoPage /></ProtectedRoute>}
      />
      {/* Painel admin em path com hash (não exposto). /admin é deliberadamente removido. */}
      <Route path="/painel-x7k2m9q4f3" element={<Suspense fallback={<div className="min-h-screen bg-[#0b1220]" />}><AdminPage /></Suspense>} />
      <Route path="/termos-de-uso" element={<TermosDeUso />} />
      <Route path="/politica-de-privacidade" element={<PoliticaDePrivacidade />} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <RouteTracker />
        <AppRouter />
        <Toaster position="top-right" richColors />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
